import { useMemo, useState } from 'react';
import { Loader, ErrorAlert, EmptyRow } from './States.jsx';
import { useGetStockAvailabilityQuery } from '../store/apiSlice.js';

const VIEWS = [
  { id: 'in', label: 'In stock' },
  { id: 'out', label: 'Out of stock' },
  { id: 'all', label: 'All' },
];

const skuKey = (row) => [row.product, row.texture, row.length, row.color].join('||');

/**
 * Live stock for every product combination: received − sold.
 *
 * Reads /stock-out/availability, which any signed-in user may call, and is
 * refreshed automatically after every Stock In or Stock Out save.
 */
export default function InventoryTable({ defaultView = 'in', maxHeight = 460 }) {
  const { data, isLoading, isError, error, refetch } = useGetStockAvailabilityQuery();
  const [view, setView] = useState(defaultView);
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState('');

  const all = data || [];
  const productNames = useMemo(() => [...new Set(all.map((row) => row.product))].sort(), [all]);
  const counts = useMemo(
    () => ({
      in: all.filter((row) => row.available > 0).length,
      out: all.filter((row) => row.available <= 0).length,
      all: all.length,
    }),
    [all]
  );

  const term = search.trim().toLowerCase();
  const rows = all
    .filter((row) => (view === 'in' ? row.available > 0 : view === 'out' ? row.available <= 0 : true))
    .filter((row) => !product || row.product === product)
    .filter((row) => !term || `${row.product} ${row.texture} ${row.length} ${row.color}`.toLowerCase().includes(term));

  const totals = rows.reduce(
    (sum, row) => ({ in: sum.in + row.qtyIn, out: sum.out + row.qtyOut, available: sum.available + row.available }),
    { in: 0, out: 0, available: 0 }
  );

  if (isLoading) return <Loader label="Loading stock…" />;
  if (isError) return <ErrorAlert message={error?.message || 'Could not load stock'} onRetry={refetch} />;

  return (
    <>
      <div className="table-toolbar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="row-actions" role="tablist" aria-label="Stock view">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`btn ${view === v.id ? 'primary' : 'ghost'}`}
              style={{ padding: '6px 12px', fontSize: 12 }}
              onClick={() => setView(v.id)}
            >
              {v.label} ({counts[v.id]})
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={product} onChange={(e) => setProduct(e.target.value)} aria-label="Filter by product" style={{ minWidth: 150 }}>
            <option value="">All products</option>
            {productNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Search texture, length, color…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search stock"
            style={{ minWidth: 220 }}
          />
        </div>
      </div>

      <div style={{ overflow: 'auto', maxHeight }}>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Texture</th>
              <th>Length</th>
              <th>Color</th>
              <th className="num">Received</th>
              <th className="num">Sold</th>
              <th className="num">In Stock</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <EmptyRow colSpan={7}>
                {all.length ? 'Nothing matches this view' : 'No stock yet — add a delivery in Stock In Management'}
              </EmptyRow>
            ) : (
              rows.map((row) => (
                <tr key={skuKey(row)}>
                  <td>{row.product}</td>
                  <td>{row.texture}</td>
                  <td>{row.length}</td>
                  <td>{row.color}</td>
                  <td className="num">{row.qtyIn}</td>
                  <td className="num">{row.qtyOut}</td>
                  <td className="num">
                    <strong style={{ color: row.available > 0 ? undefined : 'var(--danger, #b3261e)' }}>
                      {row.available}
                    </strong>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <th colSpan={4} style={{ textAlign: 'right' }}>
                  Total ({rows.length} item{rows.length === 1 ? '' : 's'})
                </th>
                <th className="num">{totals.in}</th>
                <th className="num">{totals.out}</th>
                <th className="num">{totals.available}</th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}